import React, { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";

import SerieForm from "./SerieForm";
import { SmartlistType } from "../../api/MediaAPI.types";
import SmartlistAPI from "../../api/SmartlistAPI";
import "../../css/admin/series.scss";
import { getDefaultProjectLanguage } from "../../utils/data/Content";
import {
  convertSerieAndSeasonsToValidSlug,
  generateNewSeason,
  genereateNewSerie,
  getSerieAndSeasonsErrors,
} from "../../utils/data/Series";
import { smartlistToApi } from "../../utils/smartlist";
import { PROJECTS_ROUTE } from "../routes/routes";

import type { Dispatch, JSX, SetStateAction } from "react";

import type {
  GetSmartlistResponse,
  SmartlistSeason,
  SmartlistSeasonToCreate,
  SmartlistSeasonUpdated,
  SmartlistSerieToCreate,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";

function SeriePage(): JSX.Element {
  const [serie, setSerie] = useState<GetSmartlistResponse>();
  const [seasons, setSeasons] = useState<
    (SmartlistSeason | SmartlistSeasonUpdated)[]
  >([]);
  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(0);
  const { projectSlug, id, action } = useParams<{
    projectSlug: string;
    id: string;
    action: "edit" | "new";
  }>();
  const { t } = useTranslation("editor-series");

  const history = useHistory();
  const queryClient = useQueryClient();

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const { language_uuid: languageUuid, language } = getDefaultProjectLanguage(
    languages,
    project,
  );

  const showSuccessMessage = (
    message: string,
    options?: {
      id?: string;
    },
  ): void => {
    toast.success(message, {
      id: options?.id,
    });
  };

  const showErrorMessage = useCallback(
    (
      message: string,
      options?: {
        id?: string;
      },
    ): void => {
      toast.error(message, {
        id: options?.id,
        duration: 5000,
      });
    },
    [],
  );

  const onUpdateSmartlistError = (
    errors: { message: string }[],
    type: string,
    index?: number,
  ): void => {
    if (!errors) return;

    const errorMessages = errors.map(
      (error) =>
        `${t(`${type}Error`, { rank: index })}${t("content:colon")} ${t(`error:${error.message}`)}`,
    );

    const errorDetails = errorMessages.join(", ");

    showErrorMessage(`${t("editor-notif:saveError")} - ${errorDetails}`);
  };

  const updateSmartlistMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: UpdatedSmartlist;
      onSuccess: (updatedSmartlist: GetSmartlistResponse) => void;
    }) =>
      SmartlistAPI.updateSmartlist({
        uuid,
        data: smartlistToApi(data),
        projectSlug,
      }),
    onSuccess: (updatedSmartlist, { onSuccess }) => {
      showSuccessMessage(t("editor-notif:serieSaveSuccess"), {
        id: "serieSaveSuccess",
      });

      onSuccess(updatedSmartlist);
    },
    onError: (
      errors: {
        response: {
          data?: {
            error?: { message: string }[];
          };
        };
      },
      { data },
    ) => {
      const error = errors.response.data?.error ?? [];
      if (data.type === SmartlistType.SEASON) {
        onUpdateSmartlistError(error, data.type, data.rank);
      } else {
        onUpdateSmartlistError(error, data.type);
      }
    },
  });

  const updateSerieAndSeasons = async (
    updatedSerie: UpdatedSmartlist,
    updatedSeasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
    onSuccess: () => void,
  ): Promise<void> => {
    const errorMessages = getSerieAndSeasonsErrors(
      updatedSerie as unknown as GetSmartlistResponse,
      updatedSeasons,
    );

    if (errorMessages.size > 0) {
      const errorDetails = Array.from(errorMessages).join(", ");
      showErrorMessage(errorDetails);

      return;
    }

    const {
      serie: updatedSerieWithValidSlug,
      seasons: updatedSeasonsWithValidSlug,
    } = convertSerieAndSeasonsToValidSlug(
      updatedSerie as unknown as GetSmartlistResponse,
      updatedSeasons,
    );

    try {
      await updateSmartlistMutation.mutateAsync({
        uuid: updatedSerieWithValidSlug.uuid,
        data: updatedSerieWithValidSlug,
        onSuccess,
      });

      updatedSeasonsWithValidSlug.forEach(async (season) => {
        await updateSmartlistMutation.mutateAsync({
          uuid: season.uuid,
          data: season,
          onSuccess,
        });
      });
    } catch (error) {
      console.error(error);
    }
  };

  const createSeasonMutation = useMutation({
    mutationFn: async ({
      serieId,
      season,
    }: {
      serieId: string;
      season: SmartlistSeasonToCreate;
      onSuccess: (createdSeason: SmartlistSeason) => void;
    }) =>
      SmartlistAPI.createSeason({
        serieId,
        season,
        projectSlug,
      }),
    onSuccess: (createdSeason, { onSuccess }) => {
      onSuccess(createdSeason);
    },
    onError: () => showErrorMessage(t("editor-notif:saveError")),
  });

  const deleteSeasonMutation = useMutation({
    mutationFn: async ({
      serieId,
      seasonId,
    }: {
      serieId: string;
      seasonId: string;
    }) =>
      SmartlistAPI.deleteSeason({
        serieId,
        seasonId,
        projectSlug,
      }),
    onSuccess: (_, { seasonId }) => {
      if (!serie) return;

      const updatedSerie = {
        ...serie,
        smartlist_items: serie.smartlist_items.filter(
          (item) => item.content_uuid !== seasonId,
        ),
      };
      setSerie(updatedSerie);
      setSeasons(seasons.filter((season) => season.uuid !== seasonId));
      setSelectedSeasonIndex(selectedSeasonIndex - 1);

      showSuccessMessage(t("seasonDeleteSuccess"));
    },
    onError: () => showErrorMessage(t("editor-notif:saveError")),
  });

  const onUpdate = (
    updatedSerie: UpdatedSmartlist,
    updatedSeasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
    onSuccess: () => void,
  ): void => {
    updateSerieAndSeasons(updatedSerie, updatedSeasons, onSuccess);
  };

  const onDeleteSeason = (seasonUuid: string, serieUuid: string): void => {
    deleteSeasonMutation.mutate({
      serieId: serieUuid,
      seasonId: seasonUuid,
    });
  };

  const onAddSeason = (
    smartlistUuid: string,
    season: SmartlistSeasonToCreate,
  ): void => {
    const onSuccess = (seasonCreated: SmartlistSeason): void => {
      if (!serie) return;

      const updatedSerie = {
        ...serie,
        smartlist_items: [
          ...serie.smartlist_items,
          {
            content_type: "smartlist",
            content_uuid: seasonCreated.uuid,
            rank: seasonCreated.rank,
            uuid: seasonCreated.smartlist_item_uuid,
          },
        ],
      };
      const updatedSeasons = [...seasons, seasonCreated];

      setSeasons(updatedSeasons);
      setSerie(updatedSerie);
      setSelectedSeasonIndex(updatedSeasons.length - 1);
      showSuccessMessage(t("seasonAddSuccess"));
    };

    createSeasonMutation.mutate({
      serieId: smartlistUuid,
      season,
      onSuccess,
    });
  };

  const getSmartlistQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSingleSmartlist,
    params: [{ smartlistId: id, projectSlug }],
  });

  const { data: serieData } = useQuery({
    ...getSmartlistQuery,
    onError: () => {
      showErrorMessage(t("editor-notif:fetchSerieError"), {
        id: "fetchSerieError",
      });
      history.push(`${PROJECTS_ROUTE}/${projectSlug}/series`);
    },
    enabled: !!id && action === "edit",
  });

  const getSeasonListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSeasonList,
    params: [{ serieId: serieData?.uuid ?? "", projectSlug }],
  });

  const { data: seasonsData } = useQuery({
    ...getSeasonListQuery,
    onError: () => {
      showErrorMessage(t("editor-notif:fetchSerieError"), {
        id: "fetchSerieError",
      });
      history.push(`${PROJECTS_ROUTE}/${projectSlug}/series`);
    },
    enabled: !!serieData?.uuid && action === "edit",
  });

  const createSerieMutation = useMutation({
    mutationFn: async (serieToCreate: SmartlistSerieToCreate) =>
      SmartlistAPI.createSmartlist({
        data: serieToCreate,
        projectSlug,
      }),
    onSuccess: ({ uuid }, serieToCreate) => {
      const season = generateNewSeason(serieToCreate, 1);

      createSeasonMutation.mutate({
        serieId: uuid,
        season,
        onSuccess: () => {
          const getSmartlistIndexListQuery = SmartlistAPI.generateQuery({
            fn: SmartlistAPI.getSmartlistIndexList,
            params: [
              {
                projectSlug,
                smartlistType: SmartlistType.SERIES,
              },
            ],
          });

          queryClient.invalidateQueries({
            queryKey: getSmartlistIndexListQuery.queryKey,
          });

          showSuccessMessage(t("editor-notif:createSerieSuccess"));
          history.replace(
            `${PROJECTS_ROUTE}/${projectSlug}/series/edit/${uuid}`,
          );
        },
      });
    },
    onError: () => {
      showErrorMessage(t("editor-notif:createSmartlistError"));
      history.replace(`${PROJECTS_ROUTE}/${projectSlug}/smartlist`);
    },
  });

  const hasCreatedRef = useRef(false);

  useMount(() => {
    if (action === "new" && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      const serieToCreate = genereateNewSerie({
        defaultLanguage: language,
        defaultLanguageUuid: languageUuid,
      });

      createSerieMutation.mutate(serieToCreate);
    }
  });

  useEffect(() => {
    if (seasonsData) {
      setSeasons([...seasonsData].sort((a, b) => a.rank - b.rank));
    }
  }, [seasonsData]);

  useEffect(() => {
    if (serieData) {
      setSerie({
        ...serieData,
        smartlist_items: serieData.smartlist_items,
      });
    }
  }, [serieData]);

  return (
    <div className="editor-series-update-container">
      {serie ? (
        <SerieForm
          onAddSeason={onAddSeason}
          onDelSeason={onDeleteSeason}
          onSubmit={onUpdate}
          seasons={seasons}
          selectedSeasonIndex={selectedSeasonIndex}
          serie={serie}
          setSeasons={setSeasons}
          setSelectedSeasonIndex={setSelectedSeasonIndex}
          setSerie={setSerie as Dispatch<SetStateAction<UpdatedSmartlist>>}
        />
      ) : null}
    </div>
  );
}

export default SeriePage;
